import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { walkInService, WalkInRequest } from '../../services/walkInService';
import { useToast } from '../../context/ToastContext';
import BarberHeader from '../../components/barber/BarberHeader';

interface LocationState {
    clientId: number | null;
    clientName: string | null;
    isAnonymous: boolean;
    serviceId?: number;
    serviceName: string;
    servicePrice: number;
    serviceDuration: number;
    promotionId?: number;
    isPromotion?: boolean;
    multiplier?: number;
    startDate?: string;
    startTime?: string;
}

const WalkInConfirmPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [barbersSameBranch, setBarbersSameBranch] = useState<import('../../types/barber.types').Barber[]>([]);
    const [barbersOtherBranch, setBarbersOtherBranch] = useState<import('../../types/barber.types').Barber[]>([]);
    const [assignedBarbers, setAssignedBarbers] = useState<(number | undefined)[]>([]);
    const state = location.state as LocationState;

    React.useEffect(() => {
        import('../../services/barberService').then(({ barberService }) => {
            import('../../services/authService').then(({ authService }) => {
                const currentUser = authService.getCurrentUser();
                
                Promise.all([
                    barberService.getMyStatus().catch(() => null),
                    barberService.getAllBarbers().catch(() => [])
                ]).then(([myStatus, allBarbers]) => {
                    
                    const myBranches = new Set(myStatus?.schedules?.map(s => s.branchId).filter(Boolean) || []);
                    
                    const staffBarbers = allBarbers.filter(b => b.userId !== currentUser?.id && !b.isIndependent);
                    
                    const same = staffBarbers.filter(b => b.schedules?.some(s => s.branchId && myBranches.has(s.branchId)));
                    const other = staffBarbers.filter(b => !b.schedules?.some(s => s.branchId && myBranches.has(s.branchId)));
                    
                    setBarbersSameBranch(same);
                    setBarbersOtherBranch(other);
                }).catch(console.error);
            });
        });
    }, []);

    React.useEffect(() => {
        if (state?.isPromotion && state.multiplier) {
            setAssignedBarbers(Array(state.multiplier).fill(undefined));
        }
    }, [state]);

    // Redirect if no state
    React.useEffect(() => {
        if (!state || (!state.serviceId && !state.promotionId)) {
            navigate('/barber/walk-in');
        }
    }, [state, navigate]);

    const getClientDisplayName = () => {
        if (state?.clientName) {
            return state.clientName;
        }
        return 'Cliente Walk-in';
    };

    const handleConfirm = async () => {
        if (!state) return;

        try {
            setSubmitting(true);
            const request: WalkInRequest = {
                clientId: state.clientId || undefined,
                clientName: state.isAnonymous ? (state.clientName || undefined) : undefined,
                barberServiceId: state.serviceId || 0, // Fallback, will be ignored if promotionId is present
                promotionId: state.promotionId,
                assignedBarberIds: state.isPromotion ? assignedBarbers : undefined,
                notes: notes || undefined,
                startDate: state.startDate,
                startTime: state.startTime
            };

            const response = await walkInService.createWalkIn(request);
            showToast('¡Atención registrada exitosamente!', 'success');

            // Navigate to success page
            navigate('/barber/walk-in/success', {
                state: {
                    clientDisplayName: response.clientDisplayName,
                    serviceName: response.serviceName,
                    servicePrice: response.servicePrice,
                    appointmentId: response.appointmentId
                }
            });
        } catch (error: any) {
            console.error('Error creating walk-in:', error);
            showToast(error.message || 'Error al registrar la atención', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (!state) {
        return null;
    }

    return (
        <>
            <BarberHeader
                title={state.startDate ? "📅 Agendar Turno" : "🚶 Atender Ahora"}
                subtitle={state.startDate ? "Paso 3: Confirmar reserva" : "Paso 3: Confirmar atención"}
                rightContent={
                    <button
                        className="btn btn-outline btn-sm"
                        onClick={() => navigate('/barber/dashboard')}
                    >
                        ✕ Cancelar
                    </button>
                }
            />
            <div className="container fade-in" style={{ maxWidth: '600px', margin: '0 auto', padding: '1rem' }}>

                {/* Step indicator */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    marginBottom: '2rem'
                }}>
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--success)', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>✓</div>
                    <div style={{ width: '3rem', height: '2px', backgroundColor: 'var(--success)', alignSelf: 'center' }} />
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--success)', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>✓</div>
                    <div style={{ width: '3rem', height: '2px', backgroundColor: 'var(--success)', alignSelf: 'center' }} />
                    <div style={{
                        width: '2rem', height: '2rem', borderRadius: '50%',
                        backgroundColor: 'var(--accent)', color: 'var(--text-on-accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: '700'
                    }}>3</div>
                </div>

                <button
                    onClick={() => navigate(-1)}
                    style={{
                        marginBottom: '1rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                >
                    ← Volver
                </button>

                <h2 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)' }}>📋 {state.startDate ? "Confirmar reserva" : "Confirmar atención"}</h2>

                {/* Summary card */}
                <div style={{
                    backgroundColor: 'var(--surface)',
                    borderRadius: '1rem',
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                    border: '1px solid var(--secondary)',
                    color: 'var(--text-primary)'
                }}>
                    {state.startDate && state.startTime && (
                        <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--secondary)' }}>
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Horario Programado:</span>
                            <div style={{ fontWeight: '700', fontSize: '1.25rem', marginTop: '0.25rem', color: 'var(--accent)' }}>
                                📅 {new Date(state.startDate + 'T12:00:00').toLocaleDateString('es-AR')} a las {state.startTime}
                            </div>
                        </div>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Cliente:</span>
                        <div style={{ fontWeight: '700', fontSize: '1.25rem', marginTop: '0.25rem' }}>
                            👤 {getClientDisplayName()}
                            {state.isAnonymous && (
                                <span style={{
                                    marginLeft: '0.5rem',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                    color: 'var(--warning)',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '1rem',
                                    fontWeight: '600'
                                }}>WALK-IN</span>
                            )}
                        </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{state.isPromotion ? 'Promoción:' : 'Servicio:'}</span>
                        <div style={{ fontWeight: '700', fontSize: '1.25rem', marginTop: '0.25rem', color: state.isPromotion ? 'var(--accent)' : 'inherit' }}>
                            {state.isPromotion ? '🎁 ' : '✂️ '} {state.serviceName}
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                            ⏱️ {state.serviceDuration} minutos {state.isPromotion && `(x${state.multiplier})`}
                        </div>
                    </div>

                    {state.isPromotion && state.multiplier && (
                        <div style={{ marginBottom: '1.5rem', marginTop: '1.5rem', borderTop: '1px solid var(--secondary)', paddingTop: '1rem' }}>
                            <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>Asignación de Turnos</h3>
                            {Array.from({ length: state.multiplier }).map((_, index) => (
                                <div key={index} style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <span style={{ minWidth: '80px', fontWeight: '600' }}>Corte {index + 1}:</span>
                                    {index === 0 ? (
                                        <span style={{ color: 'var(--accent)', fontWeight: '600' }}>Yo (Gestor)</span>
                                    ) : (
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', overflow: 'hidden' }}>
                                            <div style={{ display: 'flex', overflowX: 'auto', gap: '0.5rem', paddingBottom: '0.5rem', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                                                {/* Yo */}
                                                <button
                                                    onClick={() => {
                                                        const newAssigned = [...assignedBarbers];
                                                        newAssigned[index] = undefined;
                                                        setAssignedBarbers(newAssigned);
                                                    }}
                                                    style={{
                                                        padding: '0.4rem 0.8rem',
                                                        borderRadius: '1rem',
                                                        border: assignedBarbers[index] === undefined ? '1px solid var(--accent)' : '1px solid var(--secondary)',
                                                        backgroundColor: assignedBarbers[index] === undefined ? 'var(--accent)' : 'transparent',
                                                        color: assignedBarbers[index] === undefined ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                                                        whiteSpace: 'nowrap',
                                                        fontSize: '0.9rem',
                                                        cursor: 'pointer',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    Yo
                                                </button>
                                                
                                                {/* Same Branch */}
                                                {barbersSameBranch.map(b => (
                                                    <button
                                                        key={b.id}
                                                        onClick={() => {
                                                            const newAssigned = [...assignedBarbers];
                                                            newAssigned[index] = b.id;
                                                            setAssignedBarbers(newAssigned);
                                                        }}
                                                        style={{
                                                            padding: '0.4rem 0.8rem',
                                                            borderRadius: '1rem',
                                                            border: assignedBarbers[index] === b.id ? '1px solid var(--success)' : '1px solid var(--secondary)',
                                                            backgroundColor: assignedBarbers[index] === b.id ? 'var(--success)' : 'transparent',
                                                            color: assignedBarbers[index] === b.id ? 'white' : 'var(--text-primary)',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.9rem',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {b.nickname || b.firstName}
                                                    </button>
                                                ))}

                                                {/* Other Branch divider if needed, or just append with a different style */}
                                                {barbersOtherBranch.length > 0 && (
                                                    <div style={{ width: '1px', backgroundColor: 'var(--secondary)', margin: '0 0.25rem' }}></div>
                                                )}
                                                
                                                {barbersOtherBranch.map(b => (
                                                    <button
                                                        key={b.id}
                                                        onClick={() => {
                                                            const newAssigned = [...assignedBarbers];
                                                            newAssigned[index] = b.id;
                                                            setAssignedBarbers(newAssigned);
                                                        }}
                                                        title="Otras sucursales"
                                                        style={{
                                                            padding: '0.4rem 0.8rem',
                                                            borderRadius: '1rem',
                                                            border: assignedBarbers[index] === b.id ? '1px solid var(--warning)' : '1px dashed var(--secondary)',
                                                            backgroundColor: assignedBarbers[index] === b.id ? 'var(--warning)' : 'transparent',
                                                            color: assignedBarbers[index] === b.id ? 'white' : 'var(--text-secondary)',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.9rem',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {b.nickname || b.firstName}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{
                        borderTop: '1px solid var(--secondary)',
                        paddingTop: '1rem',
                        marginTop: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span style={{ fontWeight: '700', fontSize: '1.1rem' }}>Total a cobrar:</span>
                        <span style={{ fontWeight: '800', fontSize: '1.75rem', color: 'var(--accent)' }}>
                            ${state.servicePrice.toLocaleString()}
                        </span>
                    </div>
                </div>

                {/* Notes */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                        Notas (opcional)
                    </label>
                    <textarea
                        placeholder="Preferencias del cliente, observaciones..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        style={{
                            width: '100%',
                            padding: '1rem',
                            backgroundColor: 'var(--surface)',
                            border: '2px solid var(--secondary)',
                            borderRadius: '0.75rem',
                            color: 'var(--text-primary)',
                            fontSize: '1rem',
                            resize: 'none',
                            outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
                        onBlur={(e) => e.target.style.borderColor = 'var(--secondary)'}
                    />
                </div>

                {/* Confirm button */}
                <button
                    onClick={handleConfirm}
                    disabled={submitting}
                    className="btn"
                    style={{
                        width: '100%',
                        padding: '1.25rem',
                        backgroundColor: submitting ? 'var(--secondary)' : 'var(--success)',
                        color: submitting ? 'var(--text-light)' : 'white',
                        fontSize: '1.2rem',
                        fontWeight: '700',
                        cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => {
                        if (!submitting) {
                            e.currentTarget.style.filter = 'brightness(1.1)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!submitting) {
                            e.currentTarget.style.filter = 'none';
                            e.currentTarget.style.transform = 'none';
                        }
                    }}
                >
                    {submitting ? '⏳ Registrando...' : '✅ Confirmar Atención'}
                </button>
            </div>
        </>
    );
};

export default WalkInConfirmPage;
